import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { UserPlus, Copy, Ban, Crown, ShieldCheck, History, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageTitle } from "./DashboardLayout";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const input = "w-full h-10 px-3 rounded-lg border border-input text-sm focus:outline-none focus:ring-2 focus:ring-electric";
const fmt = (d) => (d ? new Date(d).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");
const roleBadge = { owner: "bg-amber-100 text-amber-700", super_admin: "bg-purple-100 text-purple-700", admin: "bg-blue-100 text-blue-700" };
const statusBadge = { pending: "bg-blue-100 text-blue-700", accepted: "bg-green-100 text-green-700", revoked: "bg-slate-100 text-slate-500", expired: "bg-red-100 text-red-600" };

export default function Users() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [tab, setTab] = useState("team");
  const [invite, setInvite] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const canManage = user?.role === "owner" || user?.role === "super_admin";

  const { data: usersData } = useQuery({ queryKey: ["staff-users"], queryFn: () => api.get("/admin/users").then((r) => r.data) });
  const { data: invData } = useQuery({ queryKey: ["invitations"], queryFn: () => api.get("/admin/invitations").then((r) => r.data.invitations) });
  const { data: history } = useQuery({ queryKey: ["role-history"], queryFn: () => api.get("/admin/role-history").then((r) => r.data.history) });
  const { data: events } = useQuery({ queryKey: ["security-events"], queryFn: () => api.get("/admin/security-events").then((r) => r.data.events) });

  const users = usersData?.users || [];
  const assignable = usersData?.assignableRoles || [];
  const refresh = () => { qc.invalidateQueries({ queryKey: ["staff-users"] }); qc.invalidateQueries({ queryKey: ["role-history"] }); qc.invalidateQueries({ queryKey: ["security-events"] }); };

  const changeRole = async (u, role) => {
    if (role === u.role) return;
    if (!window.confirm(`Change ${u.email}'s role from "${u.role}" to "${role}"? This affects their permissions immediately.`)) { refresh(); return; }
    try { await api.put(`/admin/users/${u.id}/role`, { role }); toast.success("Role updated"); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); refresh(); }
  };
  const toggleActive = async (u) => {
    const next = !(u.active !== false);
    if (!window.confirm(`${next ? "Activate" : "Deactivate"} ${u.email}? ${next ? "" : "They will be unable to log in."}`)) return;
    try { await api.put(`/admin/users/${u.id}/active`, { active: next }); toast.success(next ? "Activated" : "Deactivated"); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const transfer = async (u) => {
    if (!window.confirm(`Make ${u.email} an Owner? This grants full control of the platform.`)) return;
    try { const r = await api.post(`/admin/users/${u.id}/transfer-ownership`); toast.success(r.data.message); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const revoke = async (iv) => {
    if (!window.confirm(`Revoke the invitation for ${iv.email}?`)) return;
    try { await api.post(`/admin/invitations/${iv.id}/revoke`); toast.success("Invitation revoked"); qc.invalidateQueries({ queryKey: ["invitations"] }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const tabs = [["team", "Team"], ["invitations", "Invitations"], ["activity", "Security & Activity"]];

  return (
    <div>
      <PageTitle title="Users & Roles" subtitle="Invite staff, manage roles, and review security events" action={
        canManage ? <Button className="bg-electric hover:bg-tech gap-1.5" onClick={() => setInvite(true)} data-testid="invite-staff-btn"><UserPlus className="h-4 w-4" /> Invite Staff</Button> : null
      } />
      {!canManage && <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 px-4 py-2.5 text-sm text-amber-800 flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> You have read-only access. Only an Owner can invite staff or change roles.</div>}

      <div className="flex gap-1 border-b border-slate-200 mb-5">
        {tabs.map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`tab-${id}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === id ? "border-electric text-electric" : "border-transparent text-slate-500 hover:text-navy"}`}>{label}</button>
        ))}
      </div>

      {tab === "team" && (
        <div className="rounded-xl bg-white border border-slate-200 overflow-x-auto">
          <div className="flex justify-end px-3 py-2 border-b border-slate-100">
            <label className="flex items-center gap-2 text-xs text-slate-500 cursor-pointer" data-testid="show-inactive-toggle">
              <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} /> Show deactivated
            </label>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase font-mono"><tr><th className="text-left p-3">Name</th><th className="text-left p-3">Email</th><th className="text-left p-3">Role</th><th className="text-left p-3">Status</th><th className="p-3"></th></tr></thead>
            <tbody>
              {users.filter((u) => showInactive || u.active !== false).map((u) => {
                const isSelf = u.id === usersData?.actorId;
                const roleOpts = Array.from(new Set([u.role, ...assignable, "customer"]));
                return (
                  <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                    <td className="p-3 font-medium text-navy">{u.name}{isSelf && <span className="ml-1 text-xs text-slate-400">(you)</span>}</td>
                    <td className="p-3 text-slate-600">{u.email}</td>
                    <td className="p-3">
                      {canManage && !isSelf && u.role !== "owner" ? (
                        <select value={u.role} onChange={(e) => changeRole(u, e.target.value)} className="text-xs rounded-md border border-slate-200 px-2 py-1" data-testid={`user-role-${u.id}`}>
                          {roleOpts.map((r) => <option key={r} value={r}>{r.replace("_", " ")}</option>)}
                        </select>
                      ) : <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${roleBadge[u.role] || "bg-slate-100 text-slate-600"}`}>{u.role.replace("_", " ")}</span>}
                    </td>
                    <td className="p-3">{u.active === false ? <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-600">Deactivated</span> : <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">Active</span>}</td>
                    <td className="p-3">
                      {canManage && !isSelf && (
                        <div className="flex gap-1 justify-end">
                          {u.role !== "owner" && <button onClick={() => transfer(u)} title="Make Owner" className="p-1.5 rounded hover:bg-amber-50 text-amber-600" data-testid={`transfer-${u.id}`}><Crown className="h-4 w-4" /></button>}
                          <button onClick={() => toggleActive(u)} title={u.active === false ? "Activate" : "Deactivate"} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" data-testid={`toggle-active-${u.id}`}><Ban className="h-4 w-4" /></button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {tab === "invitations" && (
        <div className="rounded-xl bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase font-mono"><tr><th className="text-left p-3">Email</th><th className="text-left p-3">Role</th><th className="text-left p-3">Invited by</th><th className="text-left p-3">Status</th><th className="text-left p-3">Sent</th><th className="p-3"></th></tr></thead>
            <tbody>
              {(invData || []).length === 0 ? <tr><td colSpan={6} className="p-8 text-center text-slate-400">No invitations yet.</td></tr> :
                (invData || []).map((iv) => (
                  <tr key={iv.id} className="border-t border-slate-100" data-testid={`invite-row-${iv.id}`}>
                    <td className="p-3 font-medium text-navy">{iv.email}</td>
                    <td className="p-3 text-slate-600 capitalize">{iv.role.replace("_", " ")}</td>
                    <td className="p-3 text-slate-500">{iv.invitedBy}</td>
                    <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded-full capitalize ${statusBadge[iv.status]}`}>{iv.status}</span></td>
                    <td className="p-3 text-slate-500">{fmt(iv.createdAt)}</td>
                    <td className="p-3 text-right">{canManage && iv.status === "pending" && <button onClick={() => revoke(iv)} className="text-xs text-red-500 hover:underline" data-testid={`revoke-${iv.id}`}>Revoke</button>}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "activity" && (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="rounded-xl bg-white border border-slate-200 overflow-hidden">
            <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100 flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-slate-500"><History className="h-3.5 w-3.5" /> Role History</div>
            <div className="divide-y divide-slate-50 max-h-[480px] overflow-y-auto">
              {(history || []).length === 0 ? <p className="p-6 text-center text-slate-400 text-sm">No role changes yet.</p> :
                (history || []).map((h) => (
                  <div key={h.id} className="px-4 py-3 text-sm" data-testid="role-history-item">
                    <p className="text-navy"><b>{h.userEmail}</b>: {h.fromRole || "invited"} → <b className="capitalize">{(h.toRole || "").replace("_", " ")}</b></p>
                    <p className="text-xs text-slate-400">by {h.changedBy} · {fmt(h.createdAt)}</p>
                  </div>
                ))}
            </div>
          </div>
          <div className="rounded-xl bg-white border border-slate-200 overflow-hidden">
            <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100 flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-slate-500"><AlertTriangle className="h-3.5 w-3.5" /> Security Events</div>
            <div className="divide-y divide-slate-50 max-h-[480px] overflow-y-auto">
              {(events || []).length === 0 ? <p className="p-6 text-center text-slate-400 text-sm">No security events. 🎉</p> :
                (events || []).map((ev) => (
                  <div key={ev.id} className="px-4 py-3 text-sm" data-testid="security-event-item">
                    <p className="text-navy"><b className="text-red-600">{ev.kind.replace(/_/g, " ")}</b></p>
                    <p className="text-xs text-slate-400">{ev.actorEmail} ({ev.actorRole}) · {fmt(ev.createdAt)}</p>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {invite && <InviteDialog assignable={assignable} onClose={() => setInvite(false)} onSent={() => qc.invalidateQueries({ queryKey: ["invitations"] })} />}
    </div>
  );
}

function InviteDialog({ assignable, onClose, onSent }) {
  const [form, setForm] = useState({ email: "", name: "", role: assignable[0] || "technician" });
  const [link, setLink] = useState("");
  const [sending, setSending] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const r = await api.post("/admin/invitations", form);
      setLink(r.data.inviteLink);
      onSent();
      toast.success("Invitation created");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}><DialogContent><DialogHeader><DialogTitle>Invite a Staff Member</DialogTitle>
      <DialogDescription>They'll receive a secure, single-use link to verify their email and set their own password.</DialogDescription></DialogHeader>
      {link ? (
        <div className="space-y-3" data-testid="invite-result">
          <p className="text-sm text-slate-600">Invitation created. Share this secure link (also emailed to them):</p>
          <div className="flex gap-2">
            <input className={`${input} font-mono text-xs`} readOnly value={link} data-testid="invite-link" />
            <Button type="button" variant="outline" className="shrink-0 gap-1.5" onClick={() => { navigator.clipboard.writeText(link); toast.success("Copied"); }}><Copy className="h-4 w-4" /></Button>
          </div>
          <Button className="w-full bg-electric hover:bg-tech" onClick={onClose}>Done</Button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3" data-testid="invite-form">
          <input className={input} type="email" placeholder="Staff email" required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="invite-email" />
          <input className={input} placeholder="Name (optional)" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="invite-name" />
          <select className={input} value={form.role} onChange={(e) => set("role", e.target.value)} data-testid="invite-role">
            {assignable.map((r) => <option key={r} value={r}>{r.replace("_", " ")}</option>)}
          </select>
          <Button type="submit" disabled={sending} className="w-full bg-electric hover:bg-tech" data-testid="invite-submit">{sending ? "Creating…" : "Send Invitation"}</Button>
        </form>
      )}
    </DialogContent></Dialog>
  );
}
